import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { INR, CATEGORY_LABEL } from "@/lib/constants";
import { Activity, Trophy, Zap, Calendar, RefreshCw, Gift, Coins } from "lucide-react";
import RenewDialog from "@/components/RenewDialog";
import ReferDialog from "@/components/ReferDialog";
import RedeemDialog from "@/components/RedeemDialog";
import { PendingDocsPanel, GoalPanel, EffortFuelPanel, AssessmentPanel } from "@/components/MemberWellness";
import { QRCodeSVG } from "qrcode.react";

const HERO = "https://images.pexels.com/photos/6296020/pexels-photo-6296020.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function CustomerHubPage() {
  const { user } = useAuth();
  const [renewOpen, setRenewOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["customer-me"],
    queryFn: async () => (await apiClient.get("/customer/me")).data,
  });

  const profile = data?.profile;
  const m = data?.active_membership;
  const session = data?.today_session;
  const att = profile?.attendance_pct || 0;
  const attColor = att >= 80 ? "#00E676" : att >= 60 ? "#FFEA00" : "#FF3B30";
  const sessions_left = m?.total_sessions ? (m.total_sessions - (m.used_sessions || 0)) : null;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="customer-hub">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-md border border-zinc-900 bg-zinc-950">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative p-8 flex items-end justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Welcome back</div>
            <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">{user?.full_name}</h1>
            <div className="text-zinc-300 text-sm mt-2 max-w-md">
              Train hard. Recover smart. Track everything.
            </div>
          </div>
          <Button onClick={() => setRenewOpen(true)} data-testid="renew-button"
            className="bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm h-11">
            <RefreshCw size={16} className="mr-2" /> Buy / Renew Plan
          </Button>
        </div>
      </div>
      <RenewDialog open={renewOpen} onOpenChange={setRenewOpen} />

      {/* Pending policy documents — interrupts until signed */}
      <PendingDocsPanel />

      {/* Goal setting — auto-prompts new members */}
      <GoalPanel />

      {/* Personal Check-In QR — coach scans this at the gym floor */}
      <Panel data-testid="member-qr-panel">
        <PanelHeader title="Your Check-In QR" subtitle="Show this to your coach at every session — they scan it to log your attendance." />
        <div className="flex flex-col sm:flex-row items-center gap-6 p-2">
          <div className="bg-white p-4 rounded-md">
            <QRCodeSVG value={user?.id || ""} size={168} level="M" includeMargin={false} data-testid="member-qr-code" />
          </div>
          <div className="flex-1 text-sm text-zinc-400 space-y-2">
            <div><span className="text-volt font-bold">Member ID:</span> <span className="font-mono-cmd">{user?.id?.slice(0, 8)}…</span></div>
            <div>Your coach scans this from their phone when you walk in. Each check-in updates your attendance streak and unlocks retention rewards.</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Never share this QR — it uniquely identifies you.</div>
          </div>
        </div>
      </Panel>

      {/* Refer a friend banner */}
      <ReferDialog trigger={
        <button className="w-full text-left bg-gradient-to-r from-volt/10 to-zinc-950 border border-volt/30 hover:border-volt rounded-md p-5 flex items-center gap-4 transition-colors"
          data-testid="refer-friend-button">
          <div className="w-12 h-12 grid place-items-center bg-volt text-black rounded-sm">
            <Gift size={22} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-volt font-semibold">Earn REPS</div>
            <div className="font-display text-2xl font-black uppercase tracking-tight">Refer a friend → earn REPS</div>
            <div className="text-xs text-zinc-400 mt-1">Redeemable for merch, recovery, PT, guest passes & more. Earn rate set by Super Admin.</div>
          </div>
          <div className="text-volt font-display text-xl">→</div>
        </button>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* WOD */}
        <Panel className="lg:col-span-8">
          <PanelHeader subtitle="Today's Session" title={session ? session.title : "Rest day"} />
          {session ? (
            <>
              <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-4">
                <span><Calendar size={12} className="inline mr-1" /> {new Date(session.start_time).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>
                <span className="text-volt">{session.session_type}</span>
                <span>{session.duration_min} min</span>
              </div>
              {session.wod ? (
                <pre className="bg-zinc-950 border border-zinc-900 p-4 rounded-sm font-mono-cmd text-sm whitespace-pre-wrap leading-relaxed text-zinc-200" data-testid="customer-wod">
                  {session.wod}
                </pre>
              ) : (
                <div className="text-zinc-500 text-sm py-6">Your coach hasn&apos;t posted the WOD yet. Check back soon.</div>
              )}
            </>
          ) : (
            <div className="text-zinc-500 text-sm py-6">No session scheduled for today.</div>
          )}
        </Panel>

        {/* Stats */}
        <div className="lg:col-span-4 space-y-4">
          <Panel>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Attendance · 30d</div>
            <div className="flex items-baseline justify-between mt-2">
              <div className="font-display text-6xl font-black leading-none" style={{ color: attColor }}>
                {Math.round(att)}<span className="text-2xl">%</span>
              </div>
              <Activity size={28} style={{ color: attColor }} />
            </div>
            <div className="h-1.5 mt-3 bg-zinc-900 rounded-sm overflow-hidden">
              <div className="h-full" style={{ width: `${Math.min(att, 100)}%`, background: attColor }} />
            </div>
          </Panel>

          {m && (
            <Panel data-testid="customer-membership">
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Active Plan</div>
              <div className="font-display text-2xl font-black uppercase mt-1">{m.plan_name}</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{CATEGORY_LABEL[m.category]}</div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Stat label="Price" value={INR(m.price)} />
                <Stat label="Expires" value={new Date(m.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} />
                {sessions_left != null && <Stat label="Sessions Left" value={sessions_left} accent />}
                <Stat label="Status" value={m.status} />
              </div>
            </Panel>
          )}

          <Panel data-testid="loyalty-points">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Your REPS Balance</div>
            <div className="font-display text-5xl font-black text-volt mt-2 leading-none">{profile?.loyalty_points || 0}</div>
            <div className="mt-2 text-xs text-zinc-500">Earn for attendance · referrals · renewals · challenges.</div>
            <RedeemDialog trigger={
              <Button data-testid="redeem-button" className="mt-3 w-full bg-zinc-900 border border-zinc-800 hover:border-volt text-volt uppercase tracking-wider rounded-sm">
                Redeem REPS
              </Button>
            } />
          </Panel>
        </div>
      </div>

      {/* Body comp */}
      {profile?.body_comp && (
        <Panel>
          <PanelHeader title="Body Composition" subtitle="Latest reading" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat label="Height" value={`${profile.body_comp.height_cm} cm`} accent />
            <Stat label="Weight" value={`${profile.body_comp.weight_kg} kg`} accent />
            <Stat label="Body Fat" value={`${profile.body_comp.body_fat_pct}%`} accent />
            <Stat label="Muscle Mass" value={`${profile.body_comp.muscle_mass_kg} kg`} accent />
          </div>
        </Panel>
      )}

      {/* Wellness · Effort + Fuel scores */}
      <EffortFuelPanel />

      {/* Full assessments history */}
      <AssessmentPanel userId={user?.id} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-zinc-950 border border-zinc-900 p-3 rounded-sm">
      <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${accent ? "text-volt" : "text-white"}`}>{value}</div>
    </div>
  );
}
